import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { publicPortalService } from '@/services/publicPortalService';
import { QUERY_KEYS } from '@/constants';
import { connectPublicSocket, disconnectPublicSocket } from '@/services/socketService';

export function usePublicHome(options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.home(),
    queryFn: () => publicPortalService.getHome(),
    staleTime: 60_000,
    ...options,
  });
}

export function usePublicTournaments(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.tournaments(params),
    queryFn: () => publicPortalService.listTournamentsPublic(params),
    ...options,
  });
}

export function usePublicMatches(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.matches(params),
    queryFn: () => publicPortalService.listMatches(params),
    ...options,
  });
}

export function usePublicMatch(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.match(id),
    queryFn: () => publicPortalService.getMatch(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function usePublicTeams(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.teams(params),
    queryFn: () => publicPortalService.listTeams(params),
    ...options,
  });
}

export function usePublicTeam(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.team(id),
    queryFn: () => publicPortalService.getTeam(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function usePublicPlayers(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.players(params),
    queryFn: () => publicPortalService.listPlayers(params),
    ...options,
  });
}

export function usePublicPlayer(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.player(id),
    queryFn: () => publicPortalService.getPlayer(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function usePublicGrounds(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.grounds(params),
    queryFn: () => publicPortalService.listGrounds(params),
    ...options,
  });
}

export function usePublicGround(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.ground(id),
    queryFn: () => publicPortalService.getGround(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function usePublicGallery(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.gallery(params),
    queryFn: () => publicPortalService.listGallery(params),
    ...options,
  });
}

export function usePublicAlbum(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.album(id),
    queryFn: () => publicPortalService.getAlbum(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function usePublicSponsors(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.sponsors(params),
    queryFn: () => publicPortalService.listSponsors(params),
    ...options,
  });
}

export function usePublicSearch(query, options = {}) {
  const trimmed = (query || '').trim();
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.search(trimmed),
    queryFn: () => publicPortalService.search({ q: trimmed }),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
    ...options,
  });
}

export function usePublicStats(options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.PUBLIC.stats(),
    queryFn: () => publicPortalService.getStats(),
    staleTime: 120_000,
    ...options,
  });
}

export function usePublicLiveSocket({ matchIds = [], enabled = true } = {}) {
  const queryClient = useQueryClient();
  const joinedRef = useRef(new Set());
  const matchKey = matchIds.filter(Boolean).join(',');

  useEffect(() => {
    if (!enabled) return undefined;

    const ids = matchKey ? matchKey.split(',') : [];
    const socket = connectPublicSocket();

    const invalidateLive = () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PUBLIC.all });
    };

    const handleUpdate = () => invalidateLive();

    socket.on('match:update', handleUpdate);
    socket.on('connection:update', handleUpdate);
    socket.on('match:finished', handleUpdate);

    const joinMatch = (matchId) => {
      if (!matchId || joinedRef.current.has(matchId)) return;
      socket.emit('join-public-match', { matchId }, (response) => {
        if (response?.ok) {
          joinedRef.current.add(matchId);
        }
      });
    };

    const leaveMatch = (matchId) => {
      if (!matchId || !joinedRef.current.has(matchId)) return;
      socket.emit('leave-match', { matchId });
      joinedRef.current.delete(matchId);
    };

    ids.forEach(joinMatch);

    const onConnect = () => {
      ids.forEach(joinMatch);
    };

    socket.on('connect', onConnect);

    return () => {
      socket.off('match:update', handleUpdate);
      socket.off('connection:update', handleUpdate);
      socket.off('match:finished', handleUpdate);
      socket.off('connect', onConnect);
      [...joinedRef.current].forEach(leaveMatch);
      joinedRef.current.clear();
      disconnectPublicSocket();
    };
  }, [enabled, matchKey, queryClient]);
}
